import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTrash, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  useMarkMultipleNotificationsAsRead,
  useDeleteMultipleNotifications,
} from "../../hooks/notifications/useNotificationMutations";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import { useNotifications } from "../../hooks/notifications/useNotifications";

/**
 * BulkActions Component
 *
 * Provides bulk operation controls for selected notifications
 * Shows progress indicators and handles bulk mark as read/delete operations
 */
const BulkActions = ({ className = "" }) => {
  const { selectedNotifications, clearSelection, queryParams } =
    useNotificationQueryContext();
  const { data: notificationsData } = useNotifications(queryParams);
  const markMultipleAsReadMutation = useMarkMultipleNotificationsAsRead();
  const deleteMultipleMutation = useDeleteMultipleNotifications();

  const notifications = notificationsData?.data?.notifications || [];
  const selectedCount = selectedNotifications.length;
  const hasUnreadSelected = notifications.some(
    (n) => selectedNotifications.includes(n._id) && !n.read
  );

  // Handle bulk mark as read
  const handleBulkMarkAsRead = async () => {
    if (selectedCount === 0 || !hasUnreadSelected) return;

    try {
      const unreadSelectedIds = notifications
        .filter((n) => selectedNotifications.includes(n._id) && !n.read)
        .map((n) => n._id);

      if (unreadSelectedIds.length > 0) {
        await markMultipleAsReadMutation.mutateAsync(unreadSelectedIds);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedCount === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedCount} notification${
        selectedCount !== 1 ? "s" : ""
      }? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteMultipleMutation.mutateAsync(selectedNotifications);
    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  };

  // Handle clear selection
  const handleClearSelection = () => {
    clearSelection();
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className={`bulk-actions bg-blue-50 border border-blue-200 rounded-lg p-3 ${className}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">
              {selectedCount}
            </span>
          </div>
          <span className="text-sm font-medium text-gray-900">
            {selectedCount} notification{selectedCount !== 1 ? "s" : ""}{" "}
            selected
          </span>
        </div>

        <div className="flex items-center gap-2">
          {hasUnreadSelected && (
            <button
              onClick={handleBulkMarkAsRead}
              disabled={markMultipleAsReadMutation.isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors duration-200"
            >
              {markMultipleAsReadMutation.isLoading ? (
                <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faCheck} className="text-xs" />
              )}
              <span>Mark read</span>
            </button>
          )}

          <button
            onClick={handleBulkDelete}
            disabled={deleteMultipleMutation.isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors duration-200"
          >
            {deleteMultipleMutation.isLoading ? (
              <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faTrash} className="text-xs" />
            )}
            <span>Delete</span>
          </button>

          <button
            onClick={handleClearSelection}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-all duration-200"
            title="Clear selection"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>
        </div>
      </div>

      {/* Modern Progress indicator */}
      {(markMultipleAsReadMutation.isLoading ||
        deleteMultipleMutation.isLoading) && (
        <div className="mt-4 p-3 bg-white rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 text-sm text-medium-grey">
            <div className="w-5 h-5 border-2 border-deep-red border-t-transparent rounded-full animate-spin" />
            <span className="font-medium">
              {markMultipleAsReadMutation.isLoading
                ? "Marking notifications as read..."
                : "Deleting notifications..."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActions;
